import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./forms.css";
import axios from "axios";
import {
  APP_GTAG_CONVERSION_ID,
  APP_RECAPTCHA_SITE_KEY,
} from "../../utils/env";
import { useAcubeService } from "../../service/acube-api.hook";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const ContactFormInnerCard = () => {
  const [selectedOption, setSelectedOption] = useState("");
  const [formValues, setFormValues] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { postProxy } = useAcubeService();
  const { executeRecaptcha } = useGoogleReCaptcha();


  useEffect(() => {
    const handleResize = () => {
      document.body.style.display = "none";
      document.body.style.display = "block";
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    clearError(name);
  };

  const handlePhoneChange = (value) => {
    setFormValues({ ...formValues, phone: value });
    clearError("phone");
  };

  const handleSelectChange = (e) => {
    const { value } = e.target;
    setSelectedOption(value);
    clearError("selectedOption");
  };

  const clearError = (field) => {
    // Remove the specific error field
    if (errors[field]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors, [field]: null };
        // Check if there are any remaining errors after clearing the current one
        const hasRemainingErrors = Object.values(newErrors).some(
          (error) => error !== null
        );
        if (!hasRemainingErrors) {
          setErrorMessage("");
        }
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formValues.name) newErrors.name = "Name is required";
    if (!formValues.company) newErrors.company = "Company is required";
    if (!formValues.email) newErrors.email = "Email is required";
    else if (!emailRegex.test(formValues.email))
      newErrors.email = "Please enter a valid email address";
    if (!formValues.phone) newErrors.phone = "Phone number is required";
    if (!selectedOption) newErrors.selectedOption = "Please select an option";
    // Email validation using regex
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formValues.email && !emailPattern.test(formValues.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendEmail = async () => {
    const params = {
      name: formValues.name,
      company_name: formValues.company,
      email: formValues.email,
      phone: formValues.phone,
      lookingfor: selectedOption,
      message: formValues.message,
      type: "trial",
    };

    await postProxy("contact/get-touch", params)
      .then(() => {
        setSuccessMessage(
          "Thank you for reaching out! Your message has been sent successfully."
        );
        setErrorMessage("");
        if (window.gtag) {
          window.gtag("event", "conversion", {
            send_to: APP_GTAG_CONVERSION_ID + "/Mw06CIGEnIwYEOeYrKQp",
            event_category: "User",
            event_action: "Form Submission",
            event_label: "Form Button Click",
          });
        }

        setTimeout(() => {
          setSuccessMessage("");
          setFormValues({
            name: "",
            company: "",
            email: "",
            phone: "",
            message: "",
          });
        }, 5000);
        setSelectedOption("");
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("There was an error sending the email:", error);
        setSuccessMessage("");
        setErrorMessage(
          "Oops! Something went wrong while sending your message. Please try again later."
        );
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);

      let token = null;

      if (executeRecaptcha) {
        try {
          token = await executeRecaptcha('contact_form');
          if (!token) {
            setErrorMessage(
              "reCAPTCHA token is missing. Please refresh the page and try again."
            );
          } else {
            sendEmail();
          }
        } catch (recaptchaError) {
          setErrorMessage(
            "reCAPTCHA execution failed. Please refresh the page and try again."
          );
        }
      } else {
        setErrorMessage(
          "reCAPTCHA execution failed. Please refresh the page and try again."
        );
      }


    } else {
      setSuccessMessage("");
      setErrorMessage(
        "Please make sure all required fields are filled out correctly before submitting the form."
      );
      setIsLoading(false);
    }
  };
  return (
    <section className="contact-form-section py-3 py-lg-0">
      <div className="custom-container">
        <div className="card-block">
          <div className="mb-2 text-primary ft-wt-600 card-title h5">
            Schedule a Free Consultation
          </div>
          <div className="mb-4 text-primary card-subtitle h6">
            So our team can reach out to you on time
          </div>
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}
          <Form onSubmit={handleSubmit} id="contact-form-card" noValidate>
            <Form.Group
              controlId="consultationFormName"
              className="mb-4 position-relative"
            >
              <Form.Control
                type="text"
                placeholder="Name"
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group
              controlId="consultationFormCompany"
              className="mb-4 position-relative"
            >
              <Form.Control
                type="text"
                placeholder="Company / Organization"
                name="company"
                value={formValues.company}
                onChange={handleInputChange}
                isInvalid={!!errors.company}
              />
              <Form.Control.Feedback type="invalid">
                {errors.company}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group
              controlId="consultationFormEmail"
              className="mb-4 position-relative"
            >
              <Form.Control
                type="email"
                placeholder="Company Email ID"
                name="email"
                value={formValues.email}
                onChange={handleInputChange}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group
              controlId="consultationFormSelect"
              className="mb-4 position-relative"
            >
              <Form.Control
                as="select"
                aria-label="How can we help you?"
                value={selectedOption}
                onChange={handleSelectChange}
                isInvalid={!!errors.selectedOption}
              >
                <option value="" disabled hidden>
                  How Can We Help You?
                </option>
                <option value="Mobile app development">
                  Mobile app development
                </option>
                <option value="UI/UX Design">UI/UX Design</option>
                <option value="Android app development">
                  Android app development
                </option>
                <option value="IOS app development">IOS app development</option>
                <option value="Web app development">Web app development</option>
                <option value="IOT">IOT</option>
                <option value="AIML">AIML</option>
                <option value="QA Automation">QA Automation</option>
                <option value="RPA">RPA</option>
                <option value="Others">Others</option>
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.selectedOption}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group
              controlId="consultationFormPhone"
              className="mb-4 position-relative"
            >
              <PhoneInput
                country={"us"}
                inputProps={{
                  name: "phone",
                  required: true,
                  autoFocus: true,
                }}
                placeholder="Mobile Number*"
                value={formValues.phone}
                onChange={handlePhoneChange}
                isInvalid={!!errors.phone}
              />
              {errors.phone && (
                <div className="text-danger position-absolute d-block ft-12">
                  {errors.phone}
                </div>
              )}
            </Form.Group>
            <Form.Group
              controlId="consultationFormMessage"
              className="mb-4 position-relative"
            >
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Share Your Message"
                name="message"
                value={formValues.message}
                onChange={handleInputChange}
              />
            </Form.Group>
            <div className="text-center d-flex align-items-center justify-content-center">
              <Button
                variant="primary"
                type="submit"
                className="text-uppercase gap-2 d-flex align-items-center"
              >
                {isLoading && <Spinner size="sm" />}
                Get Started
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </section>
  );
};

const ContactFormCard = () => {

  return (
    <GoogleReCaptchaProvider reCaptchaKey={APP_RECAPTCHA_SITE_KEY}>
      <ContactFormInnerCard />
    </GoogleReCaptchaProvider>
  );
};


export default React.memo(ContactFormCard);
